<script setup>
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';
import CmprSegmentedControl from '@cta/frontend/components/CmprSegmentedControl.vue';
import '@campact/camper/icons/CmprIconSolidExclamationTriangle';

import { computed, inject } from 'vue';

const store = inject('store');

const props = defineProps({
  analyticsIdPrefix: {
    type: String,
    default: 'donate-call',
  },
  recurringDonationOfferCheckboxLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferHintPlacement: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferHintText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonPreselected: {
    type: Boolean,
    default: false,
  },
  recurringDonationOfferRecurringButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferRequestText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferUiElement: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['decideOnRecurringDonationOffer']);

// If the segmented control is used and the one-time option is not preselected (thus the user is forced to make a
// decision), a hint is shown  requesting the user to make a decision.
const showRequestDecisionHint = computed(() => {
  return props.recurringDonationOfferRequestText !== undefined
    && props.recurringDonationOfferUiElement === 'segmented_control'
    && props.recurringDonationOfferOnetimeButtonPreselected !== true
    && store.values.amount !== undefined;
});
</script>

<template>
  <div class="mt-90">
    <div
      v-if="props.recurringDonationOfferUiElement === 'checkbox'"
      class="recurring-donation-offer-checkbox p-50"
    >
      <div class="cmpr-checkbox-control">
        <CmprCheckbox
          id="switch-to-recurring-donation"
          v-model="store.values.switchToRecurringDonation"
          data-analytics-context="recurring-donation-offer"
          :data-analytics-id="`${analyticsIdPrefix}-form-recurring-donation-offer-checkbox`"
          data-analytics-inner-text="recurring"
        />
        <label
          class="cmpr-checkbox-control__label recurring-donation-offer-checkbox__label"
          for="switch-to-recurring-donation"
        >
          {{ props.recurringDonationOfferCheckboxLabel }}
        </label>
      </div>
      <div class="recurring-donation-offer-checkbox-checkbox__hint">
        {{ props.recurringDonationOfferHintText }}
      </div>
    </div>
    <div v-else-if="props.recurringDonationOfferUiElement === 'segmented_control'">
      <div
        v-if="props.recurringDonationOfferHintPlacement === 'above'"
        class="hint-text-above px-50 py-30 mb-60"
      >
        {{ props.recurringDonationOfferHintText }}
      </div>
      <div
        class="cmpr-segmented-control"
        data-variant="secondary"
      >
        <CmprSegmentedControl
          id="continue-with-onetime-donation"
          v-model="store.values.switchToRecurringDonation"
          data-analytics-context="recurring-donation-offer"
          data-analytics-inner-text="recurring"
          name="continue-with-onetime-donation"
          :value="false"
          @change="emit('decideOnRecurringDonationOffer')"
        />
        <label
          class="cmpr-segmented-control__label"
          for="continue-with-onetime-donation"
        >
          {{ props.recurringDonationOfferOnetimeButtonLabel }}
        </label>
        <CmprSegmentedControl
          id="switch-to-recurring-donation"
          v-model="store.values.switchToRecurringDonation"
          data-analytics-context="recurring-donation-offer"
          data-analytics-inner-text="recurring"
          name="switch-to-recurring-donation"
          :value="true"
          @change="emit('decideOnRecurringDonationOffer')"
        />
        <label
          class="cmpr-segmented-control__label"
          for="switch-to-recurring-donation"
        >
          {{ props.recurringDonationOfferRecurringButtonLabel }}
        </label>
      </div>

      <div
        v-if="showRequestDecisionHint"
        class="request-decision-hint px-50 py-30 mt-60"
      >
        <cmpr-icon-solid-exclamation-triangle />
        {{ props.recurringDonationOfferRequestText }}
      </div>

      <div
        v-if="props.recurringDonationOfferHintPlacement === 'below'"
        class="hint-text-below"
      >
        {{ props.recurringDonationOfferHintText }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.recurring-donation-offer-checkbox {
  background-color: var(--cmpr-color-white);
  border: var(--cmpr-border-width-10) solid var(--cmpr-color-neutral-40);

  &__label {
    color: var(--cmpr-color-secondary-80);
    font-weight: var(--cmpr-font-weight-30);
  }

  &__hint {
    font-size: var(--cmpr-font-size-20);
    line-height: var(--cmpr-font-line-height-40);
  }
}

.hint-text-above {
  border: var(--cmpr-border-width-10) solid var(--cmpr-color-neutral-40);
}

.request-decision-hint {
  background-color: var(--cmpr-color-success);
  color: var(--cmpr-color-white);
}
</style>
